import { SelectOptionType } from 'types';
import { ApplicationStatus } from 'const';

export enum PaymentStatus {
  UNKNOWN = 'unknown',
  ACTIVE = 'active',
  PAID = 'paid',
  FAILURE = 'failure',
  UNDEFINED = 'undefined',
}

export enum PaymentSubscriptionStatus {
  ACTIVE = 'active',
  BANK_APPROVAL_PENDING = 'bank_approval_pending',
  INITIALIZED = 'initialized',
  INITIALIZING_PLAN = 'initializing_plan',
  INITIALIZING_SUBSCRIPTION = 'initializing_subscription',
  INACTIVE = 'inactive',
  UNKNOWN = 'unknown',
  SKIPPED = 'skipped',
}

export enum ApplicationAgreementsStatus {
  ACTIVE = 'active',
  PROCESSED = 'processed',
  PENDING = 'pending',
}

export enum ApplicationAgreementsSigningMethod {
  OTP = 'OTP',
  ZOOP_ONE = 'ZOOP_ONE',
}

export enum ApplicationAgreementsTemplateMethod {
  PDL_V3 = 'pdl_v3_agreement',
  PDL_V3_SANCTION_LETTER = 'pdl_v3_sanction_letter',
  PDL_V4 = 'pdl_v4_agreement',
  PDL_V4_SANCTION_LETTER = 'pdl_v4_sanction_letter',
  PDL_V5 = 'pdl_v5_agreement',
  PDL_V5_SANCTION_LETTER = 'pdl_v5_sanction_letter',
  INSTALMENT_V2 = 'installment_v2_agreement',
  INSTALMENT_V2_SANCTION_LETTER = 'installment_v2_sanction_letter',
}

export interface IApplicationAgreementsParams {
  confirmation_code: string;
  confirmation_date: string;
}

export interface IApplicationAgreementsSigningDocuments {
  agreement: string;
  created_at: string;
  id: number;
}

export enum InitEsignStatus {
  ACTIVE = 'active',
  ERROR = 'error',
  PROCESSED = 'processed',
  PROCESSING = 'processing',
}

export enum UserFeedbackStatus {
  REJECT = 'reject',
  APPLY = 'apply',
}

export interface IUserApplicationsStore {
  applicationActive: IApplication | undefined;
  applications: IApplication[];

  loanActive: ILoan | undefined;
  loanHistory: ILoan[];
  // NEED ADD TYPES
  paymentPlan: PaymentPlan | null;

  customPaymentInfo: ICustomPaymentInfo | undefined;
  paymentLinkId: IPaymentLinkId | null;
  cashFreePaymentSession: ICashFreePaymentSession | undefined;

  loanInstallmentsActiveInfo: IInstallmentsLoanInfo | undefined;

  paymentRequestStatus: IPaymentRequestStatus;

  paymentSubscription: IPaymentSubscription | undefined;
  paymentLinks: IPaymentLinks[] | undefined;

  applicationAgreements: IApplicationAgreements[] | undefined;

  applicationAllAgreements: IApplicationAgreements[] | undefined;

  downloadApplicationAgreements?: any;

  initEsign: IInitEsign[];

  applicationStatus?: ApplicationStatus;

  fullListOfBanks: IListOfBanks[] | null;

  listOfLoanPurposes: IListOfLoanPurposes[] | null;

  listOfStates: IListOfStates[] | null;

  listOfEducations: IListOfEducations[] | null;

  listOfMaritalStatuses: IListOfMaritalStatuses[] | null;

  finbitRequest: IFinbitRequest[] | null;

  verifyBankAccount: IVerifyBankAccount[] | null;

  isLoadingLoans: boolean;
  isLoadedLoans: boolean;
  isFailedLoans: boolean;

  isLoadingApplications: boolean;
  isLoadedApplications: boolean;
  isFailedApplications: boolean;
  isPaymentSubscriptionLoaded?: boolean;

  isFailedCreateApplication: boolean;
  isShowLoader: boolean;
  isShowAgreementsLoader: boolean;

  summary?: ISummaryApplication;
}

export interface IApplication {
  id: number;
  application_id: number;
  allowed_extension_tenors: [number];
  allowed_extension_tenors_price: {
    [key: number]: number;
  };
  alternative_esign_flow: boolean;
  extensionTenorsOptions: SelectOptionType[];
  created_at: string;
  disbursed_at: string;
  matured_at: string;
  days_past_due: number;
  days_past_due_bucket: string | null;
  state: string;
  tenor: number;
  accrued_bounce_fee: number;
  accrued_bounce_fee_gst: number;
  accrued_principal: number;
  accrued_late_payment_fee: number;
  accrued_late_payment_fee_gst: number;
  accrued_processing_fee: number;
  accrued_processing_fee_gst: number;
  accrued_service_fee: number;
  accrued_service_fee_gst: number;
  accrued_interest_fee: number;
  accrued_total: number;
  principal: number;
  approved_principal: number;
  approved_tenor: number;
  calculated_discount_amount: number;
  calculated_settlement_amount: number;
  calculated_settlement_date: string;
  calculated_service_fee: number;
  calculated_late_payment_fee_daily_rate: number;

  promo_code?: string;
  loan_purpose?: string;
  target_url?: string;

  product_id: number | string;
  product_name: string;
  ref_number?: string;

  name?: string;
  params?: {};
  isPaymentSubscriptionLoaded?: boolean;

  is_repeat?: boolean;
}

export interface ILoan {
  id: number;
  application_id: number;
  allowed_extension_tenors: [number];
  allowed_extension_tenors_price: {
    [key: number]: number;
  };
  alternative_esign_flow: boolean;
  extensionTenorsOptions: SelectOptionType[];
  created_at: string;
  disbursed_at: string;
  matured_at: string;
  days_past_due: number;
  days_past_due_bucket: string | null;
  state: string;
  tenor: number;
  accrued_bounce_fee: number;
  accrued_bounce_fee_gst: number;
  accrued_principal: number;
  accrued_late_payment_fee: number;
  accrued_late_payment_fee_gst: number;
  accrued_processing_fee: number;
  accrued_processing_fee_gst: number;
  accrued_service_fee: number;
  accrued_service_fee_gst: number;
  accrued_interest_fee: number;
  accrued_total: number;
  principal: number;
  product_id: number | string;
  product_name: string;
  approved_principal: number;
  approved_tenor: number;
  calculated_discount_amount: number;
  calculated_settlement_amount: number;
  calculated_settlement_date: string;
  calculated_service_fee: number;
  calculated_late_payment_fee_daily_rate: number;
  ref_number?: string;

  name?: string;
  params: {};
  payment_link: IPaymentLinks[];
}

export interface IPaymentLink {
  loan: string;
  amount_requested: number;
  gateway: string;
}

export interface IInstallmentsLoanInfo {
  installments: IInstallments[];
  schedule: ISchedule[];
  payment_for_today: IPaymentForToday[];
}

export interface IPaymentForToday {
  active_installment_no: number;
  amount: number;
}

export interface IInstallments {
  activation_date: string;
  maturity_date: string;
  no: number;
  settlement_amount: number;
}

export interface ISchedule {
  date: string;
  repayment: number;
  repaid: number;
  need_to_repay: number;
}

export interface IApplicationAgreements {
  id: number;
  application_id: number;
  loan_id: number;
  sending_moment: string;
  state: ApplicationAgreementsStatus;
  signing_method: ApplicationAgreementsSigningMethod;
  template: ApplicationAgreementsTemplateMethod;
  params: IApplicationAgreementsParams[];
  signed_document: IApplicationAgreementsSigningDocuments;
}

export interface IPaymentRequestStatus {
  status: PaymentStatus;
}

export interface IPaymentSubscription {
  status: PaymentSubscriptionStatus;
  link: string;
  isLoadedPaymentSubscription: boolean;
  isPaymentSubscriptionLoaded: boolean;
}

export interface IPaymentLinks {
  status: string;
  link: string;
}

export interface IInitEsign {
  status: InitEsignStatus;
  link: string;
  transaction_id: string;
  request_id: number;
  id: number;
  error_code: string;
  request_state: string;
}

export interface IListOfBanks {
  code: string;
  name: string;
}

export interface IListOfLoanPurposes {
  id: string;
  name: string;
  is_enabled: boolean;
}

export interface IListOfStates {
  id: string;
  name: string;
  isActive: boolean;
}

export interface IListOfEducations {
  id: number;
  name: string;
  is_enabled: boolean;
}

export interface IListOfMaritalStatuses {
  id: number;
  name: string;
  is_enabled: boolean;
}

export interface IFinbitRequest {
  url: string;
  status: string;
  error: string;
}
export interface IPreferableLanguages {
  id: number;
  name: string;
  is_enabled: boolean;
}

export interface IVerifyBankAccount {
  account_number: string;
  ifsc_code: string;
}

export interface ICalculation {
  tenor: number;
  principal: number;
  processing_fee: number;
  processing_fee_gst: number;
  interest_fee: number;
  interest_fee_discount: number;
  discounted_interest_fee: number;
  service_fee: number;
  gst_on_service_fee: number;
  interest: number;
  settlement_amount: number;
  discounted_settlement_amount: number;
  repaid_by_client: number;
  sanction_amount: number;
  maturity_date: Date | string;
  discount: {
    principal: number;
    interest: number;
    settlement_amount: number;
  };
  annualized_irr: number;
  apr: {
    interest_service: number;
    interest_service_late: number;
    interest_service_late_bounce: number;
  };
}

export interface IUserFeedback {
  user_feedback_status: string | number | null;
  user_feedback: string | null;
  user_feedback_text: string | null;
  user_feedback_detail: any;
}

export interface IAvailableUserFeedbackDetails {
  id: number;
  enabled: boolean;
  system_name: string | null;
  ranking: number;
  name: string;
  extra: [];
  created_at: string | Date;
  updated_at: string | Date;
  last_editor: {
    type: string;
    id: number | null;
  };
}

export interface ISummaryApplicationData {
  application_apply_form_buttons: [];
  application_reject_form_buttons: [];
  available_user_feedback_details: IAvailableUserFeedbackDetails;
}

export interface ISummaryApplication {
  id: number;
  state: string;
  calculation: ICalculation;
  user_feedback: IUserFeedback;
  autorejected_at: string;
  data: ISummaryApplicationData;
  partner_link_for_client_reject: string | null;
}
export interface Installment {
  [key: number]: any;
  accrued_fee_data?: Total;
  unpaid_fee_data?: Total;
  paid_fee_data?: Total;
}

export interface Total {
  principal: number;
  service_fee: number;
  service_fee_gst: number;
  interest_fee: number;
  late_payment_fee: number;
  late_payment_fee_gst: number;
  bounce_fee: number;
  bounce_fee_gst: number;
  processing_fee: number;
  processing_fee_gst: number;
  total: number;
}

export interface ActualPaymentPlan {
  installments: Installment;
  next_payment: Total;
  total_accrued: Total;
  total_unpaid: Total;
  total_paid: Total;
}

export interface PaymentPlan {
  actual_payment_plan: ActualPaymentPlan;
  expected_payment_plan: {
    service_fee_g_s_t: number;
    service_fee_g_s_t_with_discount: number;
    tenor: number;
    principal: number;
    interest: number;
    service_fee: number;
    settlement_amount: number;
    discounted_settlement_amount: number;
    maturity_date: string;
    service_fee_discount: number;
    settlement_amount_discount: number;
    average_discount_rate: number;
    installments: Record<string, Installment>;
    sanction_amount: number;
    processing_fee: number;
    processing_fee_gst: number;
  };
  product?: string;
}

export interface ICustomPaymentInfo {
  loan: {
    id: number;
    state: string;
    ref_number: string;
    days_past_due: number;
    disbursed_at: string;
    matured_at: string;
    product_name: string;
  };
  next_payment_total: number;
  next_payment_date: string;
  total_unpaid: number;
}

export interface IPaymentLinkId {
  id: number;
}

export interface ICashFreePaymentSession {
  payment_session_id: string;
  state: string;
}
